import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { CreatePeopleProps } from '@business/interfaces/createPeople/createPeopleProps';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetPeopleByNameQuery } from '@ports/internal-services/people/peopleSlice';
import { useDebounce } from 'src/hooks/Debounce';
import { routePaths } from '@utils/constants/routePaths';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../Avatar';
import { Button } from '../Button';
import { SearchableSelectInput } from '../SearchableInput';
import { Checkbox } from '../Checkbox';

export const CreatePeople = ({
  profileUrl,
  userName,
  doneHandler,
  name,
  setName,
  // setIsMergePersonOpen,
  personId,
  setSelectedPeople,
  isFromTable = false
}: CreatePeopleProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isNameSelected, setIsNameSelected] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [selectedPersonName, setSelectedPersonName] = useState<string>('');
  const [enableAddPerson, setEnableAddPerson] = useState<boolean>(false);

  // ✅ debounce typing (400ms sweet spot)
  const debouncedSearch = useDebounce(searchText, 400);

  // ✅ API now runs only after user stops typing
  const { data: peopleDataForSearch } = useGetPeopleByNameQuery(
    { search: debouncedSearch },
    { skip: !debouncedSearch }
  );

  const familySearchOptions = useMemo(() => {
    return (
      peopleDataForSearch?.data?.rows?.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        profilePicUrl: item?.profileImage
      })) || []
    );
  }, [peopleDataForSearch?.data?.rows]);

  // When parent sets a selected name (edit case)
  useEffect(() => {
    if (name) {
      setSearchText(name);
      setSelectedPersonName(name);
    }
  }, [name]);

  // Reset when cleared
  useEffect(() => {
    if (!searchText) {
      setIsNameSelected(false);
      setSelectedPersonId('');
      setSelectedPersonName('');
    }
  }, [searchText]);

  const handlePeopleSelect = (personId: string) => {
    setSelectedPeople(prev =>
      prev.includes(personId)
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };

  if (isFromTable) {
    return (
      <div className="flex items-center space-x-3 w-full">
        {/* {profileUrl ? ( */}
        <Avatar
          alt={userName}
          className="w-10 h-10 rounded-full object-cover"
          src={profileUrl}
        />

        <div className="flex items-center gap-2 flex-1">
          <span
            className="text-sm font-medium text-[#49712B] cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              setEnableAddPerson(prev => {
                const next = !prev;
                if (!prev) {
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 0);
                }
                return next;
              });
            }}>
            {t('whoIsThis')}
          </span>
          <div
            onClick={e => {
              e.stopPropagation();
              setEnableAddPerson(prev => {
                const next = !prev;
                if (!prev) {
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 0);
                }
                return next;
              });
            }}
            className="w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer text-[#49712B] border-[#49712B]">
            <span className="text-sm font-bold">
              {enableAddPerson ? '-' : '+'}
            </span>
          </div>

          {enableAddPerson && (
            <>
              <div className="w-[130px]">
                <SearchableSelectInput
                  ref={searchInputRef}
                  name="name"
                  placeholder={t('enterName')}
                  isWhiteTheme
                  isFromCreatePeople={true}
                  value={searchText}
                  options={familySearchOptions}
                  onSearch={(value: string) => {
                    setSearchText(value);
                    setName(value);
                    setIsNameSelected(false);
                    setSelectedPersonId('');
                    setSelectedPersonName('');
                  }}
                  onChange={({ id, name }) => {
                    setName(name);
                    setSearchText(name);
                    setIsNameSelected(!!id);
                    setSelectedPersonId(id || '');
                    setSelectedPersonName(name);
                  }}
                />
              </div>
              <Button
                btnStyle="link"
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  doneHandler(
                    isNameSelected,
                    selectedPersonId,
                    selectedPersonName,
                    personId
                  );
                }}
                className="px-2 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md text-sm border border-[#DCDCDC]">
                {t('done')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Grid view: card-style rendering
  return (
    <div className="flex flex-col items-center text-center cursor-pointer">
      {/* Avatar */}
      <div
        className="flex justify-center"
        onClick={() => {
          navigate(`${routePaths.People}/${personId}/Active`);
        }}>
        {profileUrl ? (
          <Avatar
            alt={userName}
            className="w-28 h-28 rounded-full object-cover"
            src={profileUrl}
          />
        ) : (
          <SVGAssets.Avatar className="w-28 h-28" />
        )}
      </div>
      <div className="flex flex-col items-center">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#49712B] mt-2">
          <div className="w-[24px] flex items-center justify-center mr-1">
            <Checkbox
              name={`${personId}`}
              onClick={e => {
                e.stopPropagation();
              }}
              onChange={() => {
                handlePeopleSelect(personId);
              }}
              className="w-4 h-4 text-[#7F9D48] rounded focus:ring-[#7F9D48] focus:ring-2"
            />
          </div>
          <span
            className="cursor-pointer"
            onClick={e => {
              e.stopPropagation();
              setEnableAddPerson(prev => {
                const next = !prev;

                // wait for input to mount
                if (!prev) {
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 0);
                }

                return next;
              });
            }}>
            {t('whoIsThis')}
          </span>
          <div
            onClick={e => {
              e.stopPropagation();
              setEnableAddPerson(prev => {
                const next = !prev;

                // wait for input to mount
                if (!prev) {
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 0);
                }

                return next;
              });
            }}
            className="w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer text-[#49712B] border-[#49712B]">
            <span className="text-sm font-bold">
              {enableAddPerson ? '-' : '+'}
            </span>
          </div>
        </div>
        {/* <span className="text-sm text-gray-500 mt-1">{photoCount} photos</span> */}
      </div>

      {enableAddPerson && (
        <div className="flex items-center gap-1 mt-2">
          <div className="w-[130px]">
            <SearchableSelectInput
              ref={searchInputRef}
              name="name"
              placeholder={t('enterName')}
              isWhiteTheme
              isFromCreatePeople={true}
              value={searchText}
              options={familySearchOptions}
              onSearch={(value: string) => {
                setSearchText(value);
                setName(value);
                setIsNameSelected(false);
                setSelectedPersonId('');
                setSelectedPersonName('');
              }}
              onChange={({ id, name }) => {
                setName(name);
                setSearchText(name);
                setIsNameSelected(!!id);
                setSelectedPersonId(id || '');
                setSelectedPersonName(name);
              }}
            />
          </div>

          <Button
            btnStyle="link"
            type="button"
            onClick={e => {
              e.stopPropagation();
              doneHandler(
                isNameSelected,
                selectedPersonId,
                selectedPersonName,
                personId
              );
            }}
            className="px-2 py-2 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md text-sm border border-[#DCDCDC]">
            {t('done')}
          </Button>
        </div>
      )}
    </div>
  );
};
